import { googleFonts } from 'common/constants/fonts'
import EntityTypeEnum from 'common/enums/entityTypeEnum'
import { BlogPostListingInterface } from 'common/types/entities/BlogPostListingInterface'
import { ButtonInterface } from 'common/types/entities/button-interface'
import { ContentBoxInterface } from 'common/types/entities/ContentBoxInterface'
import { ContentTableInterface } from 'common/types/entities/ContentTableInterface'
import { LatestBlogPostsInterface } from 'common/types/entities/LatestBlogPostsInterface'
import { OfferPriceInterface } from 'common/types/entities/OfferPriceInterface'
import { TextInterface } from 'common/types/entities/TextInterface'
import { MakeOptional } from 'common/types/utilityTypes'
import { GoogleFontProperties } from 'common/utils/fontsUtils'
import { getNewBlogPostListingFontProperties } from 'src/shared/entities/blog-post-listing/utils/get-blog-post-listing-font-properties'
import { getButtonFontProperties as getNewButtonFontProperties } from 'src/shared/entities/button/utils/get-button-font-properties'
import { getContentBoxFontProperties } from 'src/shared/entities/content-box/utils/get-content-box-font-properties'
import { getContentTableFontProperties } from 'src/shared/entities/content-table/utils/get-content-table-font-properties'
import { FaqInterface } from 'src/shared/entities/faq/faq.schema'
import { getFaqFontProperties } from 'src/shared/entities/faq/utils/get-faq-font-properties'
import { getFormFontProperties } from 'src/shared/entities/form/utils/get-form-font-properties'
import { getOfferPriceFontProperties } from 'src/shared/entities/offer-price/utils/get-offer-price-font-properties'
import { getTextFontVariants } from 'src/shared/entities/text/utils/get-text-font-properties'
import { BaseEntityNewInterface } from '../schemas/base-entity.schema'
import { EntityInnerItemInterface } from '../schemas/entity-inner-item.schema'
import {
  BaseTextContentEntityInterface,
  PageEntityInterface,
} from '../types/base-entity-interface'
import { FormInterface } from '../types/entities/form-interface'
import { isDuplicatedGoogleFont } from './is-duplicated-google-font'

export type OptionalFontVariant = MakeOptional<
  GoogleFontProperties,
  'fontFamily'
>

export interface GlobalFontProperties {
  paragraph?: GoogleFontProperties
  heading?: GoogleFontProperties
}

export class GoogleFontsGenerator {
  public generate(
    entities: PageEntityInterface[],
    globalFontProperties: GlobalFontProperties,
  ) {
    const googleFontVariants = this.getGoogleFontVariants(
      entities,
      globalFontProperties,
    )
    return this.getFontFace(googleFontVariants)
  }

  public isGoogleFontVariant(
    fontVariant: OptionalFontVariant,
  ): fontVariant is GoogleFontProperties {
    return fontVariant.fontFamily
      ? googleFonts.includes(fontVariant.fontFamily)
      : false
  }

  protected getFontFace(fontVariants: GoogleFontProperties[]) {
    const cssArr = fontVariants.map(font => {
      const fontName = font.fontFamily.replace(/\s/g, '').toLowerCase()
      const fontWeight = `${
        font.fontWeight === '400'
          ? font.fontStyle === 'normal'
            ? 'regular'
            : ''
          : font.fontWeight
      }`
      const fileName = `${fontWeight}${
        font.fontStyle === 'italic' ? font.fontStyle : ''
      }`
      return `
          @font-face {
            font-family: "${font.fontFamily}";
            font-style: ${font.fontStyle};
            font-weight: ${font.fontWeight};
            src: url(${process.env.CLOUDFRONT_SHARED}/fonts/google-fonts/${fontName}/${fileName}.woff2) format('woff2');
          }
        `
    })
    return cssArr.join('\n').trim()
  }

  private getGoogleFontVariants(
    entities: PageEntityInterface[],
    { paragraph, heading }: GlobalFontProperties,
  ) {
    const calculatedHeading = heading || paragraph
    const googleFonts: GoogleFontProperties[] = []

    for (const entity of entities) {
      const entityFontVariants: OptionalFontVariant[] =
        this.getEntityFontVariants(
          entity,
          calculatedHeading?.fontFamily,
          paragraph?.fontFamily,
        )

      if (entityFontVariants.length > 0) {
        entityFontVariants.forEach(fv => {
          let fontToPush: GoogleFontProperties | undefined

          if (!fv.fontFamily) {
            if (entity.type === EntityTypeEnum.Headline && calculatedHeading) {
              fontToPush = {
                fontFamily: calculatedHeading.fontFamily,
                fontWeight: fv.fontWeight || calculatedHeading.fontWeight,
                fontStyle: fv.fontStyle || calculatedHeading.fontStyle,
              }
            } else if (paragraph) {
              fontToPush = {
                fontFamily: paragraph.fontFamily,
                fontWeight: fv.fontWeight || paragraph.fontWeight,
                fontStyle: fv.fontStyle || paragraph.fontStyle,
              }
            }
          } else if (this.isGoogleFontVariant(fv)) {
            fontToPush = fv
          }

          if (
            fontToPush &&
            this.isGoogleFontVariant(fontToPush) &&
            !isDuplicatedGoogleFont(fontToPush, googleFonts) &&
            fontToPush.fontFamily &&
            fontToPush.fontWeight
          ) {
            googleFonts.push(fontToPush)
          }
        })
      } else {
        const fallback =
          entity.type === EntityTypeEnum.Headline
            ? calculatedHeading
            : paragraph
        if (fallback && !isDuplicatedGoogleFont(fallback, googleFonts)) {
          googleFonts.push(fallback)
        }
      }
    }

    return googleFonts
  }

  private getEntityFontVariants(
    entity: BaseEntityNewInterface | EntityInnerItemInterface,
    fallbackHeadingFontFamily?: string,
    fallbackParagraphFontFamily?: string,
  ) {
    switch (entity.type) {
      case EntityTypeEnum.ContentTable:
        return getContentTableFontProperties(entity as ContentTableInterface)
      case EntityTypeEnum.Faq:
        return getFaqFontProperties(entity as FaqInterface)
      case EntityTypeEnum.Headline:
        return getTextFontVariants(
          entity as TextInterface,
          fallbackHeadingFontFamily,
        )
      case EntityTypeEnum.Text:
      case EntityTypeEnum.BulletList:
        return getTextFontVariants(
          entity as TextInterface,
          fallbackParagraphFontFamily,
        )
      case EntityTypeEnum.OfferPrice:
        return getOfferPriceFontProperties(entity as OfferPriceInterface)
      case EntityTypeEnum.ContentBox:
        return getContentBoxFontProperties(entity as ContentBoxInterface)
      case EntityTypeEnum.Button:
      case EntityTypeEnum.ContactUsButton:
      case EntityTypeEnum.PaymentButton:
      case EntityTypeEnum.UpsellAgreeButton:
      case EntityTypeEnum.UpsellDisagreeButton:
      case EntityTypeEnum.WebinarCallToAction:
      case EntityTypeEnum.SalesRedirectionButton:
        return getNewButtonFontProperties(entity as ButtonInterface)
      case EntityTypeEnum.BlogPostListing:
      case EntityTypeEnum.LatestBlogPosts:
        return getNewBlogPostListingFontProperties(
          entity as BlogPostListingInterface | LatestBlogPostsInterface,
        )
      case EntityTypeEnum.Form:
        return getFormFontProperties(entity as FormInterface)
      default:
        return GoogleFontsGenerator.getBaseTextEntityFontVariants(
          entity as BaseTextContentEntityInterface,
        )
    }
  }

  static getBaseTextEntityFontVariants(
    entity: Pick<
      BaseTextContentEntityInterface,
      | 'fontFamily'
      | 'fontStyle'
      | 'fontWeight'
      | 'mobileFontFamily'
      | 'mobileFontStyle'
      | 'mobileFontWeight'
    >,
  ) {
    const fontProperties = entity.fontFamily
      ? [
          {
            fontFamily: entity.fontFamily,
            fontStyle: entity.fontStyle,
            fontWeight: entity.fontWeight,
          },
        ]
      : []

    if (entity.mobileFontFamily) {
      fontProperties.push({
        fontFamily: entity.mobileFontFamily,
        fontStyle: entity.mobileFontStyle,
        fontWeight: entity.mobileFontWeight,
      })
    }

    return fontProperties
  }
}

export interface GlobalSettingsFontProperties
  extends Partial<GoogleFontProperties> {
  headingFontFamily?: string
  headingFontStyle?: string
  headingFontWeight?: string
  headingFontFileId?: number
  fontFileId?: number
}
